import { newId, type StoreCode } from '@familyhub/shared';
import { api } from '../api/client';
import { db, type ListItemRecord, type SuggestionRecord } from '../db';
import { stamp } from '../db/clock';
import { enqueue } from '../db/outbox';
import { sync } from '../sync/engine';

/**
 * Everything the list screen can do.
 *
 * Each action queues one mutation and asks the sync engine to flush. Nothing
 * here awaits the network: the local write has already happened by the time the
 * function returns, which is what makes the app usable in a shop with no signal
 * and indistinguishable from one with signal.
 */

/**
 * Strictly monotonic, not `Date.now()`. Two taps in the same millisecond would
 * otherwise tie on `updated_at` *and* on the device, and last-write-wins
 * discards the second — see `db/clock.ts`.
 */
const now = stamp;

async function currentUserId(): Promise<string> {
  const row = await db.meta.get('user_id');
  const id = row?.value as string | undefined;
  if (!id) throw new Error('not signed in');
  return id;
}

/** Fire and forget: a failed flush leaves the mutation queued, which is the point. */
function flush(): void {
  void sync();
}

export async function addItem(params: {
  genericProductId: string;
  variantId?: string | null;
  storeId?: string | null;
  quantity?: number;
  note?: string | null;
  suggestionReason?: string | null;
}): Promise<string> {
  // Client-generated id: an offline add already has its final identity, so
  // replay needs no temp-id reconciliation (API §1).
  const id = newId();
  await enqueue({
    id: newId(),
    op: 'upsert',
    table: 'list_items',
    row: {
      id,
      store_id: params.storeId ?? null,
      generic_product_id: params.genericProductId,
      variant_id: params.variantId ?? null,
      quantity: params.quantity ?? 1,
      note: params.note ?? null,
      added_by_user: await currentUserId(),
      suggestion_reason: params.suggestionReason ?? null,
      updated_at: now(),
    },
  });
  flush();
  return id;
}

type ItemChange = Partial<{
  quantity: number;
  note: string | null;
  store_id: string | null;
  variant_id: string | null;
}>;

/**
 * The last row this phone queued, per id.
 *
 * The same problem `intended` solves for a quantity, one level up and for the
 * whole row: **a local write is not reliably visible to the next local read.**
 * Dexie's transaction promise settles before the commit is observable, so a
 * second edit reads the row as it was *before* the first one — and because
 * last-write-wins is per row, that second edit then sends every field back at
 * its old value with a newer timestamp. The first edit is not merely lost
 * locally; it is undone on the server.
 *
 * Two edits to one item within a few tens of milliseconds is not a contrived
 * case. It is what the add screen's card is for — tap **+**, then write a note
 * — and it is what the detail sheet has always allowed. Measured from the
 * browser suite: 39ms apart, and the note still read `quantity: 1` and shipped
 * it, silently reverting the increment on both phones.
 *
 * A memo, not a cache of the database: it is consulted only while it is
 * strictly newer than what Dexie can see, and dropped the moment Dexie catches
 * up or the other phone moves past it — which is the same last-write-wins rule
 * everything else here follows, applied to this phone's own two answers.
 */
const queuedRow = new Map<string, ListItemRecord>();

/**
 * What this phone believes a row is: the newer of what Dexie can see and what
 * it last queued itself.
 *
 * A tombstone always wins, whatever the memo says. Deletes are remove-wins, and
 * an item wrongly resurrected is bought twice.
 */
async function readRow(id: string): Promise<ListItemRecord | null> {
  const stored = await db.list_items.get(id);
  if (stored?.deleted_at) return null;
  const mine = queuedRow.get(id);
  if (!mine) return stored ?? null;
  if (!stored || Date.parse(mine.updated_at) > Date.parse(stored.updated_at)) return mine;
  queuedRow.delete(id);
  return stored;
}

/**
 * Queue one edit to a list item, without flushing.
 *
 * Last-write-wins is per row, so a change to one field still sends the whole
 * row (DATABASE §12) — read-modify-write locally, exactly as the REST PATCH
 * handler does server-side. Which is exactly why the read has to be `readRow`
 * rather than Dexie directly: the fields this change is *not* about are being
 * rewritten too, and stale ones overwrite good ones.
 *
 * Split out so `adjustQuantity` can queue without flushing: it serialises taps
 * on one row, and holding the next tap behind a network round-trip would make
 * a slow connection feel like an unresponsive button.
 */
async function queueItemPatch(id: string, change: ItemChange): Promise<boolean> {
  const existing = await readRow(id);
  if (!existing) return false;
  const row = {
    id: existing.id,
    store_id: 'store_id' in change ? (change.store_id ?? null) : existing.store_id,
    generic_product_id: existing.generic_product_id,
    variant_id: 'variant_id' in change ? (change.variant_id ?? null) : existing.variant_id,
    quantity: change.quantity ?? existing.quantity,
    note: 'note' in change ? (change.note ?? null) : existing.note,
    added_by_user: existing.added_by_user,
    suggestion_reason: existing.suggestion_reason,
    updated_at: now(),
  };
  await enqueue({ id: newId(), op: 'upsert', table: 'list_items', row });
  queuedRow.set(id, { ...row, deleted_at: null });
  return true;
}

async function patchItem(id: string, change: ItemChange): Promise<void> {
  if (await queueItemPatch(id, change)) flush();
}

export async function setQuantity(id: string, quantity: number): Promise<void> {
  if (quantity < 1) return removeItem(id);
  await patchItem(id, { quantity });
}

/**
 * The free-text line on an item — "de kleine verpakking", "voor Lisa".
 *
 * Blank is stored as null rather than an empty string, so "no note" is one
 * value everywhere instead of two that render the same and compare differently.
 */
export async function setNote(id: string, note: string | null): Promise<void> {
  const trimmed = note?.trim();
  await patchItem(id, { note: trimmed ? trimmed : null });
}

/**
 * One at a time, per row.
 *
 * Read-modify-write on a list item has to see the previous write, and nothing
 * else guarantees that. `useLiveQuery` is asynchronous, so the value React last
 * rendered is stale by the time a second tap lands; and wrapping the read and
 * the write in one Dexie transaction is not enough either — two overlapping
 * transactions still both read the old quantity, which the browser suite
 * demonstrated and a `console.log` in the middle "fixed", the way timing bugs
 * always are.
 *
 * So the ordering is stated rather than inferred: adjustments to one row queue
 * behind each other. There is one tab and one thread, and two taps on the same
 * row *are* sequential from the point of view of the person doing the tapping.
 *
 * Keyed by row id, so ticking the milk never waits on the bananas. The entry is
 * dropped when its own chain is the current tail, which keeps the map the size
 * of what is genuinely in flight rather than the size of the list.
 */
const pending = new Map<string, Promise<unknown>>();

function serialize<T>(key: string, work: () => Promise<T>): Promise<T> {
  const previous = pending.get(key) ?? Promise.resolve();
  // `.then(work, work)`: a failed adjustment must not wedge every later tap on
  // that row behind a promise nobody will ever settle.
  const result = previous.then(work, work);

  const tail = result.then(
    () => undefined,
    () => undefined,
  );
  pending.set(key, tail);
  void tail.then(() => {
    // Only if nothing queued behind it in the meantime.
    if (pending.get(key) === tail) pending.delete(key);
  });

  return result;
}

/**
 * What this phone has decided a row's quantity is, while taps keep arriving.
 *
 * It exists rather than re-reading Dexie because **a write is not reliably
 * visible to the next read**. Even with the taps strictly serialised — the
 * second one entering nine milliseconds after the first one's `enqueue` had
 * resolved — `db.list_items.get()` still returned the pre-write row, stale
 * `updated_at` and all. Dexie's transaction promise settles before the commit
 * is observable to a fresh read, so read-modify-write across two transactions
 * drops the first write however carefully the two are ordered.
 *
 * The entry is dropped once the chain for that row drains, so this never
 * becomes a second copy of the list. It is not a cache of the database: it is
 * the intent the person is expressing with their thumb, which is precisely the
 * thing the database has not caught up with yet.
 */
const intended = new Map<string, number>();

/**
 * Nudge a quantity up or down by one.
 *
 * Relative, not absolute. `setQuantity(id, item.quantity + 1)` computes the new
 * value from whatever React last rendered, so tapping **+** three times at the
 * speed a person actually taps produced two — a dropped tap on a phone,
 * invisible on a laptop where clicks are slow enough for the screen to catch up.
 */
export async function adjustQuantity(id: string, delta: number): Promise<void> {
  const queued = await serialize(id, async () => {
    const existing = await readRow(id);
    if (!existing) return false;

    // `intended` first: the previous tap's write may not be readable yet.
    const from = intended.get(id) ?? existing.quantity;
    const next = from + delta;
    intended.set(id, next);

    if (next >= 1) return queueItemPatch(id, { quantity: next });

    // "One fewer than one" means the item is gone: quantity has a
    // CHECK (quantity > 0) and there is no zero state.
    await enqueue({ id: newId(), op: 'delete', table: 'list_items', row: { id, updated_at: now() } });
    queuedRow.delete(id);
    return true;
  });

  // The chain for this row has drained, so the next tap starts from whatever is
  // actually stored — including anything the other phone has sent since.
  if (!pending.has(id)) intended.delete(id);

  // Outside the queue: flush opens a network round-trip, and holding the next
  // tap behind it would make a slow connection feel like an unresponsive button.
  if (queued) flush();
}

/**
 * Moving between stores is an edit, never a delete and re-add — that is what
 * makes "bought elsewhere" teach history correctly (ARCHITECTURE §6.6).
 *
 * **The variant moves with it.** A list item carries the store's own product,
 * so leaving `variant_id` pointing at the shop you just moved away from would
 * file an Albert Heijn product under Lidl and show AH's price beside it — the
 * exact thing the comparison view was opened to check. Passing `variantId:
 * null` is the honest state for a store where the household has no known
 * product yet: the item is generic-only there, and shows no price rather than
 * the wrong one.
 */
export async function moveToStore(
  id: string,
  storeId: string | null,
  variantId: string | null = null,
): Promise<void> {
  await patchItem(id, { store_id: storeId, variant_id: variantId });
}

export async function removeItem(id: string): Promise<void> {
  await enqueue({ id: newId(), op: 'delete', table: 'list_items', row: { id, updated_at: now() } });
  queuedRow.delete(id);
  flush();
}

/**
 * Hide a product from "Jullie producten" (migration 041). Not a delete: list
 * items, purchases and prices that point at it keep working, and picking the
 * same product off a shop's shelf later un-hides it. The whole row is sent —
 * per-row LWW — from what this phone holds; the server never trusts the
 * fields it derives (organic) and keeps the ones a phone never has (raw).
 */
export async function hideVariant(id: string): Promise<void> {
  const row = await db.product_variants.get(id);
  if (!row || row.deleted_at) return;
  await enqueue({
    id: newId(),
    op: 'upsert',
    table: 'product_variants',
    row: {
      id: row.id,
      generic_product_id: row.generic_product_id,
      store_id: row.store_id,
      name: row.name,
      brand: row.brand,
      size_value: row.size_value,
      size_unit: row.size_unit,
      ean: row.ean,
      external_id: row.external_id,
      image_url: row.image_url,
      classification_source: row.classification_source ?? 'auto',
      cross_store_product_id: row.cross_store_product_id ?? null,
      hidden: true,
      updated_at: now(),
    },
  });
  flush();
}

/**
 * Ticking means bought (ARCHITECTURE §6.6). `store` is passed only for an
 * **Any** item — that is where the store gets resolved, and what teaches
 * history which shop this household actually buys milk at.
 *
 * Idempotent by construction: the mutation id is generated once and replayed
 * from the outbox, and the server's partial unique index on
 * purchases(list_item_id) means both phones ticking the milk record one
 * purchase.
 */
export async function checkOff(listItemId: string, store?: StoreCode | null): Promise<void> {
  await enqueue({
    id: newId(),
    op: 'checkoff',
    list_item_id: listItemId,
    store: store ?? null,
    purchased_at: now(),
  });
  // Bought: the row is tombstoned, and nothing this phone queued before that
  // may be read back as though the item were still on the list.
  queuedRow.delete(listItemId);
  flush();
}

/**
 * Accepting a suggestion.
 *
 * It leaves the suggestion list the instant it joins the real one — a
 * suggestion still sitting there after it was taken reads as though the tap did
 * nothing. The reason travels onto the row, so the list can still say why the
 * thing is there.
 *
 * Lives here rather than on a screen because two screens make this offer now:
 * the list, at the bottom, and the add screen, where an empty search box is the
 * one moment somebody has said "I want to add something" and there is nothing
 * else to show them.
 *
 * It returns the id of the row it created, because on the add screen an
 * accepted suggestion is an add like any other and gets the same card
 * underneath the search field — which needs something to point at.
 */
export async function acceptSuggestion(
  suggestion: SuggestionRecord,
): Promise<{ itemId: string; storeId: string | null; imagePath: string | null }> {
  await db.suggestions.delete(suggestion.generic_product_id);
  // The suggested variant is the household's usual product, and a variant's
  // store moves with it — filing it under Overal would leave a shop's carton
  // with no shop. A variant this phone does not hold (or that was removed)
  // goes generic-only: no shop beats the wrong shop.
  const variant = suggestion.suggested_variant_id
    ? await db.product_variants.get(suggestion.suggested_variant_id)
    : undefined;
  const usable = variant && !variant.deleted_at ? variant : null;
  const storeId = usable?.store_id ?? null;
  const itemId = await addItem({
    genericProductId: suggestion.generic_product_id,
    variantId: usable?.id ?? null,
    storeId,
    suggestionReason: suggestion.reason,
  });
  return { itemId, storeId, imagePath: usable?.image_path ?? null };
}

/**
 * Refusing one. It goes at once — nagging is how people learn to ignore an app
 * — and the server is told so the mute lengthens (ARCHITECTURE §7).
 *
 * Fire and forget on the request: it is already off the screen, and a dismissal
 * that never lands just means it may be suggested again tomorrow.
 */
export async function dismissSuggestion(suggestion: SuggestionRecord): Promise<void> {
  await db.suggestions.delete(suggestion.generic_product_id);
  await api(`/api/v1/suggestions/${suggestion.generic_product_id}/dismiss`, {
    method: 'POST',
    body: {},
  }).catch(() => undefined);
}

/**
 * Aisle order (API §5.1). The whole order is replaced: a drag-and-drop screen
 * has the full list in hand, and sending it whole avoids reconciling
 * individual moves. Positions are sparse so a later insert needs no renumber.
 */
export async function saveCategoryOrder(storeId: string, categories: string[]): Promise<void> {
  // One instant for the whole order, because it is one edit. These are
  // different rows, so they cannot tie with each other.
  const at = now();
  for (const [index, category] of categories.entries()) {
    await enqueue({
      id: newId(),
      op: 'upsert',
      table: 'store_category_order',
      row: { store_id: storeId, category, position: (index + 1) * 10, updated_at: at },
    });
  }
  flush();
}

/**
 * Undo a check-off (ARCHITECTURE §6.6).
 *
 * Not "I didn't buy it" — an unbought item simply stays on the list, and there
 * is deliberately no gesture for that. This is "I didn't mean to tap that",
 * which currently records a purchase that never happened and teaches history
 * from it.
 *
 * Two paths, and the offline one is the important one: a mis-tap happens in a
 * shop, which is exactly where there is no signal.
 *
 *   - **Still queued** — the check-off has not reached the server, so undoing
 *     it is dropping the mutation and restoring the row. Nothing was recorded,
 *     so there is nothing to correct.
 *   - **Already sent** — the server has the purchase, so it has to remove it.
 *     That needs the network, and the time-box lives there.
 */
export async function undoCheckOff(listItemId: string): Promise<'undone' | 'needs_connection'> {
  const queued = await db.outbox
    .filter((entry) => entry.mutation.op === 'checkoff' && entry.mutation.list_item_id === listItemId)
    .toArray();

  if (queued.length > 0) {
    await db.transaction('rw', [db.outbox, db.list_items], async () => {
      for (const entry of queued) await db.outbox.delete(entry.seq!);
      const item = await db.list_items.get(listItemId);
      // Back on the list, exactly where it was a moment ago.
      if (item) await db.list_items.put({ ...item, deleted_at: null, updated_at: now() });
    });
    return 'undone';
  }

  try {
    await api(`/api/v1/list/items/${listItemId}/undo-checkoff`, { method: 'POST', body: {} });
  } catch {
    // Already on the server and no way to reach it. Saying so is better than a
    // local restore that the next pull would silently reverse.
    return 'needs_connection';
  }

  const item = await db.list_items.get(listItemId);
  if (item) await db.list_items.put({ ...item, deleted_at: null, updated_at: now() });
  flush();
  return 'undone';
}
